import { useCallback, useMemo, useState } from 'react';
import { FormFieldValues } from '../types/booking';
import { FormField } from '../types/rest';

type UseBookingFormProps = {
	formFieldData: FormField[];
	submitAppointment: (formFields: FormFieldValues) => Promise<{ success: boolean; fieldErrors?: Record<string, string>; appointmentId?: string }>;
	gtmEventBookAppointment: (appointmentId: string, formFields: FormField[]) => void;
};

export const useBookingForm = ({ formFieldData, submitAppointment, gtmEventBookAppointment }: UseBookingFormProps) => {
	const [step, setStep] = useState(0);
	const [fields, setFields] = useState(formFieldData);

	const totalSteps = useMemo(() => fields.reduce((max, field) => (field.step > max ? field.step : max), 0) + 1, [fields]);

	const nextStep = useCallback(() => {
		setStep((prevStep) => prevStep + 1);
	}, []);

	const previousStep = useCallback(() => {
		setStep((prevStep) => (prevStep > 0 ? prevStep - 1 : 0));
	}, []);

	const updateFieldValue = useCallback((name: string, value: string | string[] | boolean) => {
		setFields((prevFields) =>
			prevFields.map((field) => {
				if (field.name === name) {
					if (field.type === 'checkbox') {
						return { ...field, checked: value as boolean, error: undefined };
					}
					return { ...field, value: value as string, error: undefined };
				}
				return field;
			}),
		);
	}, []);

	const setFieldErrors = useCallback((errors: Record<string, string>) => {
		setFields((prevFields) =>
			prevFields.map((field) => ({
				...field,
				error: errors[field.name] || undefined,
			})),
		);
	}, []);

	const clearFieldErrors = useCallback(() => {
		setFields((prevFields) =>
			prevFields.map((field) => ({
				...field,
				error: undefined,
			})),
		);
	}, []);

	const getFormFieldValues = useCallback(() => {
		return fields.reduce((acc, field) => {
			if (field.type === 'checkbox') {
				acc[field.name] = field.checked || false;
			} else if (Array.isArray(field.value)) {
				// Filter out empty strings from array values
				acc[field.name] = field.value.filter((val) => val !== '');
			} else {
				acc[field.name] = field.value || '';
			}
			return acc;
		}, {} as FormFieldValues);
	}, [fields]);

	const groupFieldsByRow = (fieldsToGroup: FormField[]) => {
		const grouped = new Map<number, FormField[]>();
		fieldsToGroup.forEach((field) => {
			if (!grouped.has(field.row)) {
				grouped.set(field.row, []);
			}
			grouped.get(field.row)!.push(field);
		});
		return Array.from(grouped.values());
	};

	const groupedFields = useMemo(() => {
		const currentStepFormFields = fields.filter((field: FormField) => field.step === step);
		return groupFieldsByRow(currentStepFormFields);
	}, [fields, step]);

	const submitForm = useCallback(async () => {
		clearFieldErrors();
		const formFieldValues = getFormFieldValues();
		const result = await submitAppointment(formFieldValues);

		if (!result.success && result.fieldErrors) {
			setFieldErrors(result.fieldErrors);

			// Find the first field with an error and navigate to its step
			const firstErrorField = fields.find((field) => field.name in result.fieldErrors!);
			if (firstErrorField) {
				setStep(firstErrorField.step);
			}
		} else if (result.success && result.appointmentId) {
			gtmEventBookAppointment(result.appointmentId, fields);
		}

		return result;
	}, [clearFieldErrors, fields, getFormFieldValues, gtmEventBookAppointment, setFieldErrors, submitAppointment]);

	return {
		fields,
		groupedFields,
		nextStep,
		previousStep,
		step,
		submitForm,
		totalSteps,
		updateFieldValue,
	};
};
